import * as React from "react";
import { TextField, InputAdornment, Autocomplete } from "@mui/material";
import NumberFormat, { NumberFormatProps } from "react-number-format";
import { fetchData } from "../common/services/ApiClient";
import { ApiBaseUrl } from "../config";
import { TextFieldProps } from "@mui/material/TextField/TextField";
import { styled } from "@mui/material/styles";

export interface Props {
  currentValue: string | number;
  fieldName: string;
  form: any;
  setForm: (object: any) => void;
  inputFocus?: (focus: boolean) => void;
  label?: string;
  currency?: boolean;
  percentage?: boolean;
  startIcon?: React.ReactElement;
  endIcon?: React.ReactElement;
  additionalStyles?: string;
  autoSuggest?: boolean;
  placeholder?: string;
  showLabel?: boolean;
  error?: boolean;
  helperText?: string;
  regEx?: string;
  disabled?: boolean;
  apiPath?: string;
  uniqueField?: string;
  filter?: string;
}
const Root = styled("div")(({ theme }) => ({
  "& .MuiAutocomplete-root": {
    width: "100%",
  },
  "& .MuiAutocomplete-inputRoot": {
    backgroundColor: "white",
  },
  "& .MuiAutocomplete-paper": {
    "boxShadow": theme.shadows[5],
    "& .MuiAutocomplete-listbox": {
      paddingTop: 8,
      paddingBottom: 8,
    },
    "& li": {
      padding: "0px 16px",
    },
  },
  "& .MuiAutocomplete-option": {
    color: theme.palette.primary.main,
    backgroundColor: "white",
  },
  "& .MuiAutocomplete-popper": {
    zIndex: 100,
    marginTop: -15,
  },
  "& .MuiAutocomplete-listbox": {
    backgroundColor: "white",
  },
  "& input.MuiInputBase-input::placeholder": {
    color: theme.palette.primary.main,
    opacity: 1,
  },
  "marginBottom": "1rem",
}));

interface NumberFormatCustomProps {
  inputRef: (instance: NumberFormat<NumberFormatProps> | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
}

const NumberFormatCustom = React.forwardRef<
  NumberFormat<any>,
  NumberFormatCustomProps
>((props: NumberFormatCustomProps, ref) => {
  const { name, onChange, ...other } = props;

  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      onValueChange={(values: any) => {
        onChange({
          target: {
            name: props.name,
            value: values.value,
          },
        });
      }}
      thousandSeparator
      isNumericString
    />
  );
});

function TextFilterField({
  currentValue,
  fieldName,
  label,
  placeholder,
  showLabel,
  error,
  helperText,
  regEx,
  currency,
  percentage,
  disabled,
  autoSuggest,
  apiPath,
  startIcon,
  endIcon,
  additionalStyles,
  form,
  uniqueField,
  filter,
  inputFocus,
  setForm,
}: Props) {
  const [results, setResults] = React.useState([""]);
  const [closeAutoSuggest, setCloseAutoSuggest] = React.useState(false);
  const [inputValue, setInputValue] = React.useState("");

  const handleKeyPress = (e: any) => {
    if (e === "Enter") {
      setCloseAutoSuggest(true);
    }
  };

  const handleChange = (event: string) => {
    if (regEx === undefined || (regEx && RegExp(regEx).test(event))) {
      setForm({ ...form, [fieldName]: event });
    }
  };

  const noLabel = placeholder && !showLabel ? { "aria-label": label } : "";
  const inputStartIcon = startIcon
    ? {
        startAdornment: (
          <InputAdornment position="start">{startIcon}</InputAdornment>
        ),
      }
    : "";
  const inputEndIcon = endIcon
    ? {
        endAdornment: <InputAdornment position="end">{endIcon}</InputAdornment>,
      }
    : "";

  const currencyFormat = currency
    ? { inputComponent: NumberFormatCustom as any }
    : "";

  const textFieldProps: TextFieldProps = {
    id: percentage
      ? `text-filter-${fieldName}-percentage`
      : fieldName
        ? `text-filter-${fieldName}`
        : undefined,
    label: placeholder && !showLabel ? "" : label,
    placeholder,
    disabled,
    error,
    helperText,
    fullWidth: true,
    value: currentValue,
    size: "small",
    margin: "dense",
    variant: "outlined",
    onFocus: () => (inputFocus ? inputFocus(true) : undefined),
    onBlur: () => (inputFocus ? inputFocus(false) : undefined),
    onKeyPress: (e: any) => {
      handleKeyPress(e.key);
    },
    onChange: (e: any) => {
      const inputVal = e.target.value;
      handleChange(inputVal);
    },
  };

  React.useEffect(() => {
    if (autoSuggest && String(currentValue).length >= 3) {
      let isMounted = true;
      setCloseAutoSuggest(false);
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/${
            apiPath ? apiPath : "institutions"
          }?autocomplete=${fieldName.toLocaleUpperCase()}:${currentValue}${
            filter ? `&filters=${filter}` : ""
          }&fields=${fieldName.toLocaleUpperCase()}&limit=10&get_unique=${
            uniqueField ? uniqueField : fieldName.toLocaleUpperCase()
          }`,
        )
          .then((response) => {
            if (isMounted && response.highlight) {
              const suggestionArray: any[] = [];
              Object.keys(response.highlight).forEach((key: string) => {
                Object.keys(response.highlight[key]).forEach(
                  (matchingField: string) => {
                    suggestionArray.push(
                      response.highlight[key][matchingField][0].replace(
                        /(<([^>]+)>)/gi,
                        "",
                      ),
                    );
                  },
                );
              });
              setResults(Array.from(new Set(suggestionArray)).slice(0, 10));
            }
          })
          .catch((err: Error) => {
            console.error(err);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [
    apiPath,
    autoSuggest,
    currentValue,
    fieldName,
    filter,
    inputValue,
    uniqueField,
  ]);
  const filteredResults = results.filter((val: string) => val !== "");

  return (
    <Root>
      {autoSuggest ? (
        <Autocomplete
          freeSolo
          style={{ padding: 0 }}
          disablePortal
          id={`text-filter-${fieldName}`}
          disableClearable
          inputValue={inputValue}
          onInputChange={(event: any, newInputValue: any) => {
            setInputValue(newInputValue);
            handleChange(newInputValue);
          }}
          value={currentValue as string}
          options={
            String(currentValue).length >= 3 && !closeAutoSuggest
              ? filteredResults
              : []
          }
          limitTags={2}
          renderInput={(params: any) => (
            <TextField {...params} {...textFieldProps} />
          )}
        />
      ) : (
        <TextField
          className={additionalStyles}
          {...textFieldProps}
          InputProps={{
            ...noLabel,
            ...inputStartIcon,
            ...inputEndIcon,
            ...currencyFormat,
          }}
        />
      )}
    </Root>
  );
}

export default TextFilterField;
